import { usePayments } from './usePayments';
import { RiskFlag } from '../types';

interface RisksParams {
    status?: string;
    severity?: string;
    type?: string;
}

interface RiskWithPayment extends RiskFlag {
    payment?: {
        id: string;
        expected_amount: number;
        currency: string;
        provider_label: string;
    };
}

export const useRisks = (params?: RisksParams) => {
    // Get payments with unresolved risks
    const { data, isLoading, error } = usePayments({
        has_risks: true,
    });

    // Flatten all risks from payments and attach payment info
    const risks: RiskWithPayment[] = data?.data?.flatMap(payment =>
        payment.risks.map(risk => ({
            ...risk,
            payment: {
                id: payment.id,
                expected_amount: payment.expected_amount,
                currency: payment.currency,
                provider_label: payment.provider_label,
            }
        }))
    ) || [];

    let filteredRisks = risks;

    // Filter by severity (case-insensitive)
    if (params?.severity) {
        const severity = params.severity;
        filteredRisks = filteredRisks.filter(risk =>
            risk.severity.toLowerCase() === severity.toLowerCase()
        );
    }

    // Filter by type (case-insensitive)
    if (params?.type) {
        const type = params.type;
        filteredRisks = filteredRisks.filter(risk =>
            risk.type.toLowerCase() === type.toLowerCase()
        );
    }

    // Filter by status
    if (params?.status) {
        const status = params.status;
        if (status === 'resolved') {
            filteredRisks = filteredRisks.filter(risk => risk.resolved === true);
        } else if (status === 'pending') {
            filteredRisks = filteredRisks.filter(risk => risk.resolved === false);
        }
        // 'ignored' status not implemented in backend yet
    }

    const criticalCount = risks.filter(r => r.severity === 'critical').length;
    const highCount = risks.filter(r => r.severity === 'high').length;

    return {
        data: {
            data: filteredRisks,
            total: filteredRisks.length,
            critical_count: criticalCount,
            high_count: highCount,
        },
        isLoading,
        error,
    };
};
